import React, { useState } from "react";
import { FiCheckCircle, FiMail } from "react-icons/fi";
import { forgotPasswordApi } from "../../services/authApi";
import { AuthButton, AuthError, AuthField, AuthHeader, AuthLink, AuthSwitch } from "./authUi";

interface ResetPasswordProps {
  onBackToLogin: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      setFieldError("Enter your email.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await forgotPasswordApi(email.trim());
      setSentTo(email.trim());
    } catch (err: any) {
      setError(err?.response?.data?.message || "Couldn't send the reset email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <>
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-400/15 text-emerald-300">
          <FiCheckCircle size={24} />
        </div>
        <AuthHeader
          title="Check your email"
          subtitle={
            <>
              We sent a password reset link to <strong className="text-slate-200">{sentTo}</strong>.
              It can take a minute; check spam if you don't see it.
            </>
          }
        />
        <AuthSwitch>
          <AuthLink onClick={onBackToLogin}>Back to log in</AuthLink>
        </AuthSwitch>
      </>
    );
  }

  return (
    <>
      <AuthHeader
        title="Reset your password"
        subtitle="Enter the email you signed up with and we'll send you a reset link."
      />
      <form onSubmit={handleSubmit} noValidate>
        <AuthError message={error} />
        <AuthField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.com"
          icon={<FiMail />}
          value={email}
          error={fieldError}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldError("");
            setError("");
          }}
        />
        <AuthButton loading={loading}>Send reset link</AuthButton>
      </form>
      <AuthSwitch>
        Remembered it? <AuthLink onClick={onBackToLogin}>Back to log in</AuthLink>
      </AuthSwitch>
    </>
  );
};

export default ResetPassword;
